// @ts-ignore
import kuromoji from '@huid/kuromoji';
import TinySegmenter from 'tiny-segmenter';
import { API_CONFIG } from '../../config/env';
import { createTokenizeError } from './error';
import { debug, info } from './logger';
import { loadFromSessionStorage, saveToSessionStorage } from './cache';
import { ProgressCallback } from './types';

// 分词器实例
let tokenizer: any = null;
let tokenizerPromise: Promise<any> | null = null;

// 备用分词器
const segmenter = new TinySegmenter();

/**
 * 从缓存加载词典数据
 */
const loadDictionaryFromCache = (): ArrayBuffer | null => {
  const cachedDict = loadFromSessionStorage('kuromojiDict');
  if (!cachedDict) return null;

  try {
    const buffer = new Uint8Array(cachedDict).buffer;
    info('从缓存加载词典成功');
    return buffer;
  } catch (error) {
    debug('从缓存加载词典失败', error);
    sessionStorage.removeItem('kuromojiDict');
    return null;
  }
};

/**
 * 保存词典数据到缓存
 */
const saveDictionaryToCache = (dicBuffer: ArrayBuffer) => {
  try {
    const array = Array.from(new Uint8Array(dicBuffer));
    saveToSessionStorage('kuromojiDict', array);
    info('词典已缓存到 SessionStorage');
  } catch (error) {
    debug('缓存词典失败', error);
  }
};

/**
 * 获取分词器实例
 * @param onProgress 进度回调函数
 */
export const getTokenizer = async (onProgress?: ProgressCallback): Promise<any> => {
  if (tokenizer) return tokenizer;

  if (!tokenizerPromise) {
    info('初始化日语分词引擎...');
    onProgress?.(0, '正在初始化分词引擎...');

    // 尝试从缓存加载词典
    const cachedDict = loadDictionaryFromCache();
    if (cachedDict) {
      onProgress?.(30, '正在加载缓存词典...');
    }

    tokenizerPromise = new Promise((resolve, reject) => {
      const builderOptions = {
        dicPath: API_CONFIG.KUROMOJI_DICT_PATH
      };

      if (cachedDict) {
        Object.assign(builderOptions, { dicData: cachedDict });
      } else {
        onProgress?.(20, '正在下载词典文件...');
      }

      kuromoji.builder(builderOptions)
        .build((err: Error | null, _tokenizer: any) => {
          if (err) {
            debug('分词引擎初始化失败', err);
            tokenizerPromise = null;
            reject(createTokenizeError('分词引擎初始化失败', { error: err }));
            return;
          }

          tokenizer = _tokenizer;
          onProgress?.(90, '正在完成初始化...');

          // 如果使用的是远程词典，则缓存到 SessionStorage
          if (!cachedDict && _tokenizer.dicData) {
            saveDictionaryToCache(_tokenizer.dicData);
          }

          info('分词引擎初始化完成');
          onProgress?.(100, '初始化完成');
          resolve(tokenizer);
        });
    });
  }

  return tokenizerPromise;
};

/**
 * 日语分词
 * @param text 要分词的文本
 * @param onProgress 进度回调函数
 */
export const tokenizeJapanese = async (
  text: string,
  onProgress?: ProgressCallback
): Promise<string[]> => {
  if (!text || text.trim() === '') {
    return [];
  }

  debug(`开始分词: "${text}"`);

  try {
    // 尝试使用 kuromoji 进行分词
    const analyzer = await getTokenizer(onProgress);
    const tokens = analyzer.tokenize(text);

    // 提取分词结果
    const segments = tokens.map((token: any) => token.surface_form);
    debug(`kuromoji分词结果: [${segments.join(', ')}]`);

    return segments;
  } catch (error) {
    debug('kuromoji分词失败，回退到TinySegmenter', error);

    // 回退到 TinySegmenter
    return segmenter.segment(text).filter((s: string) => s.trim().length > 0);
  }
};

/**
 * 使用 kuromoji 进行词性分析
 * @param word 要分析的单词
 */
export const analyzeWordType = async (word: string) => {
  if (!word || word.trim() === '') {
    return null;
  }

  try {
    const analyzer = await getTokenizer();
    const tokens = analyzer.tokenize(word);

    if (tokens.length === 0) return null;

    // 如果只有一个token，直接返回其词性信息
    if (tokens.length === 1) {
      const token = tokens[0];
      return {
        pos: token.pos,
        pos_detail_1: token.pos_detail_1,
        pos_detail_2: token.pos_detail_2,
        pos_detail_3: token.pos_detail_3,
        conjugated_type: token.conjugated_type,
        conjugated_form: token.conjugated_form,
        basic_form: token.basic_form || token.surface_form,
        reading: token.reading,
        pronunciation: token.pronunciation
      };
    }

    // 如果有多个token，查找最匹配的那个
    const exactMatch = tokens.find((t: any) => t.surface_form === word);
    if (exactMatch) {
      return {
        pos: exactMatch.pos,
        pos_detail_1: exactMatch.pos_detail_1,
        pos_detail_2: exactMatch.pos_detail_2,
        pos_detail_3: exactMatch.pos_detail_3,
        conjugated_type: exactMatch.conjugated_type,
        conjugated_form: exactMatch.conjugated_form,
        basic_form: exactMatch.basic_form || exactMatch.surface_form,
        reading: exactMatch.reading,
        pronunciation: exactMatch.pronunciation
      };
    }

    // 如果没有精确匹配，返回第一个token的信息
    return {
      pos: tokens[0].pos,
      pos_detail_1: tokens[0].pos_detail_1,
      pos_detail_2: tokens[0].pos_detail_2,
      pos_detail_3: tokens[0].pos_detail_3,
      conjugated_type: tokens[0].conjugated_type,
      conjugated_form: tokens[0].conjugated_form,
      basic_form: tokens[0].basic_form || tokens[0].surface_form,
      reading: tokens[0].reading,
      pronunciation: tokens[0].pronunciation
    };
  } catch (error) {
    debug(`词性分析失败: "${word}"`, error);
    return null;
  }
};

/**
 * 判断是否是修饰词
 */
export const isModifier = async (word: string): Promise<boolean> => {
  const analysis = await analyzeWordType(word);
  return analysis ? (analysis.pos === '形容詞' || analysis.pos === '副詞' || analysis.pos === '助詞') : false;
};

/**
 * 判断是否是形容词
 */
export const isAdjective = async (word: string): Promise<boolean> => {
  const analysis = await analyzeWordType(word);
  return analysis ? analysis.pos === '形容詞' : false;
};